import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Clock, Linkedin, Link2, Loader2, Mail, MessageCircle, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { categoryBySlug, readingTime } from "@/articlesData";
import { DIAG_URL } from "@/links";
import { trackEvent } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareButtons = ({ title }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const encoded = encodeURIComponent(url);
  const text = encodeURIComponent(title);
  const links = [
    { id: "whatsapp", icon: MessageCircle, href: `https://wa.me/?text=${text}%20${encoded}`, label: "WhatsApp" },
    { id: "linkedin", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`, label: "LinkedIn" },
    { id: "x", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encoded}&text=${text}`, label: "X" },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Link copiado!");
      } catch {
        toast.error("Não foi possível copiar o link.");
      }
      document.body.removeChild(ta);
    }
  };
  return (
    <div data-testid="article-share" className="flex items-center gap-2">
      <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.2em] text-navy/50">Compartilhar</span>
      {links.map((l) => (
        <a
          key={l.id}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`article-share-${l.id}`}
          title={`Compartilhar no ${l.label}`}
          className="flex h-9 w-9 items-center justify-center border border-navy/20 text-navy/60 transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
        >
          <l.icon size={14} />
        </a>
      ))}
      <button
        onClick={copy}
        data-testid="article-share-copy"
        title="Copiar link"
        className="flex h-9 w-9 items-center justify-center border border-navy/20 text-navy/60 transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
      >
        <Link2 size={14} />
      </button>
    </div>
  );
};

const Block = ({ b }) => {
  if (b.t === "h2") {
    return (
      <h2 className="mt-14 font-display text-2xl font-bold tracking-tight text-navy first:mt-0 sm:text-3xl">
        {b.text}
      </h2>
    );
  }
  if (b.t === "list") {
    return (
      <ul className="mt-6 space-y-2.5 border-l-2 border-brand-orange/50 pl-6">
        {b.items.map((item, i) => (
          <li key={i} className="text-base leading-relaxed text-navy/75 sm:text-lg">{item}</li>
        ))}
      </ul>
    );
  }
  if (b.t === "numlist") {
    return (
      <ol className="mt-8 space-y-6">
        {b.items.map((item, i) => (
          <li key={i} className="flex gap-5">
            <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center border border-brand-orange/60 font-mono text-xs text-brand-orange">
              {i + 1}
            </span>
            <div>
              <p className="font-display text-lg font-bold text-navy">{item.lead}</p>
              <p className="mt-1.5 text-base leading-relaxed text-navy/70">{item.text}</p>
            </div>
          </li>
        ))}
      </ol>
    );
  }
  return <p className="mt-6 text-base leading-[1.85] text-navy/80 first:mt-0 sm:text-lg">{b.text}</p>;
};

const NewsletterBox = ({ articleSlug }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done

  const submit = async (e) => {
    e.preventDefault();
    if (state !== "idle") return;
    setState("sending");
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim(), article: articleSlug });
      trackEvent("newsletter_signup", { method: "blog_article" });
      setState("done");
    } catch {
      setState("idle");
      toast.error("Não foi possível concluir a inscrição. Confira o e-mail digitado.");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="newsletter-success" className="mt-14 border border-navy bg-navy px-8 py-10 text-center">
        <p className="font-display text-xl font-bold text-white">Inscrição confirmada.</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">
          Você recebe um e-mail de boas-vindas agora — e um aviso sempre que sair texto novo.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="newsletter-box" className="mt-14 border border-navy bg-navy px-8 py-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-orange">Receba os próximos textos</p>
      <p className="mt-4 font-display text-xl font-bold leading-snug text-white sm:text-2xl">
        Um e-mail quando sair análise nova. <span className="font-serif font-medium italic text-brand-orange">Só isso.</span>
      </p>
      <form onSubmit={submit} className="mt-7 flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com.br"
          data-testid="newsletter-email-input"
          className="flex-1 border border-white/15 bg-ink px-5 py-3.5 text-sm text-white placeholder:text-slate-600 focus:border-brand-orange focus:outline-none"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid="newsletter-submit"
          className="inline-flex items-center justify-center gap-2 bg-brand-orange px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-red disabled:opacity-60"
        >
          {state === "sending" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Quero receber
        </button>
      </form>
    </div>
  );
};

export default function Artigo() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    setArticle(null);
    axios
      .get(`${API}/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <main data-testid="article-loading" className="flex min-h-[70vh] items-center justify-center gap-3 bg-white pt-32 text-navy/50">
        <Loader2 size={18} className="animate-spin" /> Carregando texto...
      </main>
    );
  }

  if (status === "notfound") {
    return (
      <main data-testid="article-not-found" className="flex min-h-[70vh] flex-col items-center justify-center bg-white px-6 pt-32 text-center">
        <h1 className="font-display text-3xl font-bold text-navy">Conteúdo não encontrado</h1>
        <Link to="/conteudo" data-testid="article-back-404" className="mt-6 font-mono text-xs uppercase tracking-[0.22em] text-brand-orange hover:text-navy">
          ← Voltar para Conteúdo
        </Link>
      </main>
    );
  }

  const cat = categoryBySlug(article.category);
  const pullAt = article.pullQuoteAfter ?? 4;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      data-testid="article-page"
      className="bg-white"
    >
      {/* faixa navy fixa atrás do header transparente (o site tem header claro p/ fundos escuros) */}
      <div aria-hidden className="fixed inset-x-0 top-0 z-40 h-20 bg-ink" />

      {/* HEADER */}
      <section className="relative overflow-hidden bg-white pb-12 pt-32 lg:pt-44">
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <Link
              to="/conteudo"
              data-testid="article-back"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-navy/50 transition-colors hover:text-brand-orange"
            >
              <ArrowLeft size={13} /> Conteúdo
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span data-testid="article-category" className="border border-brand-orange/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange">
                {cat?.name}
              </span>
              <span data-testid="article-reading-time" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-navy/50">
                <Clock size={12} /> {readingTime(article)} min de leitura
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <h1 data-testid="article-title" className="mt-7 font-display text-3xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {article.title}
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p data-testid="article-headline" className="mt-6 font-serif text-lg font-medium italic leading-relaxed text-navy/70 sm:text-xl">
              {article.headline}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-8 border-t border-navy/10 pt-6">
              <ShareButtons title={article.title} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MÍDIA */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            {article.videoUrl ? (
              <div data-testid="article-video" className="aspect-video overflow-hidden border border-navy/10">
                <iframe
                  src={article.videoUrl}
                  title={article.title}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="relative overflow-hidden border border-navy/10">
                <img
                  src={article.image}
                  alt={article.imageAlt}
                  data-testid="article-image"
                  className="max-h-[70vh] w-full object-cover"
                />
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* CORPO */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-6" data-testid="article-content">
          {article.content.slice(0, pullAt).map((b, i) => (
            <Block key={i} b={b} />
          ))}

          {/* FRASE DESTAQUE */}
          <Reveal>
            <blockquote
              data-testid="article-pullquote"
              className="my-14 border-l-2 border-brand-orange py-2 pl-8"
            >
              <p className="font-serif text-2xl font-medium italic leading-snug text-navy sm:text-3xl">
                “{article.pullQuote}”
              </p>
            </blockquote>
          </Reveal>

          {article.content.slice(pullAt).map((b, i) => (
            <Block key={i + pullAt} b={b} />
          ))}

          {/* LINKS / REFERÊNCIAS */}
          {article.links.length > 0 && (
            <div data-testid="article-links" className="mt-14 space-y-3">
              {article.links.map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`article-link-${i}`}
                  className="group flex items-center justify-between gap-4 border border-navy/15 bg-navy/[0.03] px-6 py-5 transition-colors duration-300 hover:border-brand-orange/60"
                >
                  <span className="text-sm font-semibold text-navy transition-colors group-hover:text-brand-orange">{l.label}</span>
                  <ArrowUpRight size={16} className="shrink-0 text-navy/40 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-brand-orange" />
                </a>
              ))}
            </div>
          )}

          {/* TAGS + SHARE */}
          <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-navy/10 pt-8">
            <div data-testid="article-tags" className="flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span key={t} className="border border-navy/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-navy/60">
                  #{t}
                </span>
              ))}
            </div>
            <ShareButtons title={article.title} />
          </div>

          {/* NEWSLETTER */}
          <NewsletterBox articleSlug={article.slug} />
        </div>
      </section>

      {/* CTA */}
      <section className="noise relative overflow-hidden border-t border-white/5 bg-ink py-20">
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Descubra onde a sua{" "}
              <span className="font-serif font-medium italic text-brand-orange">marca está hoje.</span>
            </h2>
            <div className="mt-8">
              <Link
                to={DIAG_URL}
                data-testid="article-cta-diagnostico"
                className="group inline-flex items-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
              >
                Fazer o diagnóstico gratuito
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
